import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { safeFormatDate } from '../lib/dateTime';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  Mail,
  Search,
  MapPin,
  Phone,
  FileText,
  Users,
  Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import SurchargeRulesPanel from '../components/SurchargeRulesPanel';
import RateManager from '../components/RateManager';
import {
  BtTag,
  OriginBadge,
  CountrySelect,
  CustomerEInvoiceStatus,
  TAX_CATEGORIES,
  ADDRESS_SCHEMES,
  countryName,
  taxCategoryLabel,
} from '../components/EInvoiceFields';

// Leeres Formular – alle Rechnungsfelder null = Firmenstandard (leer heißt erben)
const emptyCustomerForm = () => ({
  name: '',
  description: '',
  contact_email: '',
  contact_phone: '',
  customer_number: '',
  trading_name: '',
  address_line1: '',
  address_line2: '',
  postal_code: '',
  city: '',
  state: '',
  country: 'Deutschland',
  country_code: 'DE',
  location_note: '',
  ust_id: '',
  steuernummer: '',
  billing_address_line1: '',
  billing_address_line2: '',
  billing_postal_code: '',
  billing_city: '',
  billing_country_code: null,
  bg_zuordnung: '',
  wz_code: '',
  contacts: [],
  sites: [],
  // Rechnungsempfang (X4)
  invoice_email: '',
  einvoice_address_scheme: null,
  buyer_reference: '',
  buyer_accounting_ref: '',
  // Referenzen (X3)
  contract_ref: '',
  order_ref: '',
  quote_ref: '',
  // Bankkonto (X2): leer = Standardkonto
  bank_account_id: null,
  // R6: interner Kunde – nie abrechnen
  is_internal: false,
  // Rechnungseinstellungen
  billing_interval: null,
  payment_term_days: null,
  detail_level: null,
  position_split: null,
  vat_rate: null,
  tax_category: null,
  reference_type: null,
  reference_value: '',
  require_reference: null,
  mileage_mode: null,
  mileage_rate_per_km: null,
  mileage_flat_rate: null,
});

const customerToForm = (c) => {
  const f = emptyCustomerForm();
  for (const key of Object.keys(f)) {
    if (c[key] === undefined || c[key] === null) continue;
    f[key] = c[key];
  }
  if (!c.country_code) f.country_code = c.country && c.country !== 'Deutschland' ? null : 'DE';
  return f;
};

// Textfelder, die leer als null gespeichert werden (= erben bzw. nicht gesetzt)
const NULLABLE_TEXT_FIELDS = [
  'customer_number', 'trading_name', 'invoice_email', 'buyer_reference', 'buyer_accounting_ref',
  'contract_ref', 'order_ref', 'quote_ref', 'reference_value', 'ust_id', 'steuernummer',
];

export default function CustomersPage() {
  const { api, user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  
  // Detail view state
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Invoice defaults for showing company standards
  const [invoiceDefaults, setInvoiceDefaults] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]);
  

  const canManage = user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'accounting';

  const [formData, setFormData] = useState(emptyCustomerForm());
  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const set = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    fetchCustomers();
    fetchInvoiceDefaults();
    api.get('/bank-accounts')
      .then((res) => setBankAccounts(res.data || []))
      .catch(() => setBankAccounts([]));
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/enriched');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvoiceDefaults = async () => {
    try {
      const response = await api.get('/company-settings/invoice-defaults');
      setInvoiceDefaults(response.data || {});
    } catch (error) {
      console.error('Failed to fetch invoice defaults:', error);
      setInvoiceDefaults({});
    }
  };

  const openCreateDialog = () => {
    setEditingCustomer(null);
    setFormData(emptyCustomerForm());
    setShowBillingAddress(false);
    setIsDialogOpen(true);
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData(customerToForm(customer));
    setShowBillingAddress(!!customer.billing_address_line1);
    setIsDialogOpen(true);
  };

  const openDetailView = (customer) => {
    setDetailCustomer(customer);
    setIsDetailOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // contacts und sites sind jetzt Teil von formData
      const payload = { ...formData };
      
      // Rechnungseinstellungen: Leere Felder als null senden
      // Zahlenfelder: leerer String oder undefined → null
      const numericFields = ['payment_term_days', 'vat_rate', 'mileage_rate_per_km', 'mileage_flat_rate'];
      for (const field of numericFields) {
        if (payload[field] === '' || payload[field] === undefined) {
          payload[field] = null;
        }
      }
      
      // Textfelder: leerer String → null (erbt bzw. nicht gesetzt)
      for (const field of NULLABLE_TEXT_FIELDS) {
        if (typeof payload[field] === 'string' && payload[field].trim() === '') {
          payload[field] = null;
        }
      }

      // Land: Code ist maßgeblich (BT-55), der Name bleibt als Text für das PDF
      if (payload.country_code) {
        payload.country = countryName(payload.country_code);
      }

      // Rechnungsanschrift abgeschaltet → Felder leeren
      if (!showBillingAddress) {
        payload.billing_address_line1 = '';
        payload.billing_address_line2 = '';
        payload.billing_postal_code = '';
        payload.billing_city = '';
        payload.billing_country_code = null;
      }

      // Steuerkategorie ungleich Regelsatz → Steuersatz 0 (Normregel BR-AE-05 u. a.)
      if (payload.tax_category && payload.tax_category !== 'S') {
        payload.vat_rate = null;
      }
      
      // Select-Felder mit "Firmenstandard" (leerer Wert) → null
      const selectFields = ['billing_interval', 'detail_level', 'position_split', 'reference_type', 'mileage_mode', 'tax_category', 'einvoice_address_scheme', 'bank_account_id'];
      for (const field of selectFields) {
        if (payload[field] === '' || payload[field] === 'default') {
          payload[field] = null;
        }
      }

      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, payload);
        toast.success('Kunde aktualisiert');
      } else {
        await api.post('/customers', payload);
        toast.success('Kunde erstellt');
      }

      setIsDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Speichern';
      toast.error(message);
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Möchten Sie diesen Kunden wirklich löschen?')) return;

    try {
      await api.delete(`/customers/${customerId}`);
      toast.success('Kunde gelöscht');
      fetchCustomers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Löschen';
      toast.error(message);
    }
  };

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.contact_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Helper: Primärer Ansprechpartner
  const getPrimaryContact = (customer) => {
    if (customer.contacts && customer.contacts.length > 0) {
      return customer.contacts[0].name || customer.contacts[0].email || '—';
    }
    return customer.contact_email || '—';
  };

  // Helper: Ort formatieren
  const formatLocation = (customer) => {
    const parts = [customer.postal_code, customer.city].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '—';
  };

  // Helper: Volle Adresse formatieren
  const formatFullAddress = (customer) => {
    const lines = [];
    if (customer.address_line1) lines.push(customer.address_line1);
    if (customer.address_line2) lines.push(customer.address_line2);
    const cityLine = [customer.postal_code, customer.city].filter(Boolean).join(' ');
    if (cityLine) lines.push(cityLine);
    if (customer.state) lines.push(customer.state);
    if (customer.country_code || customer.country) {
      lines.push(customer.country_code ? `${countryName(customer.country_code)} (${customer.country_code})` : customer.country);
    }
    return lines.length > 0 ? lines : null;
  };

  // Helper: Rechnungsadresse formatieren
  const formatBillingAddress = (customer) => {
    const lines = [];
    if (customer.billing_address_line1) lines.push(customer.billing_address_line1);
    if (customer.billing_address_line2) lines.push(customer.billing_address_line2);
    const cityLine = [customer.billing_postal_code, customer.billing_city].filter(Boolean).join(' ');
    if (cityLine) lines.push(cityLine);
    return lines.length > 0 ? lines : null;
  };
  
  // Helper: Prüft, ob der Kunde individuelle Rechnungseinstellungen hat
  const hasCustomInvoiceSettings = (customer) => {
    const fields = [
      'billing_interval', 'payment_term_days', 'detail_level', 'vat_rate',
      'reference_type', 'reference_value', 'require_reference',
      'mileage_mode', 'mileage_rate_per_km', 'mileage_flat_rate',
      'tax_category', 'buyer_reference', 'buyer_accounting_ref', 'contract_ref', 'order_ref', 'quote_ref',
      'bank_account_id', 'position_split'
    ];
    return fields.some(f => customer[f] != null && customer[f] !== '');
  };
  
  // Helper: Formatiert billing_interval für Anzeige
  const formatBillingInterval = (value) => {
    const labels = { monthly: 'Monatlich', quarterly: 'Quartalsweise', none: 'Nach Aufwand' };
    return labels[value] || value;
  };
  
  // Helper: Formatiert detail_level für Anzeige
  const formatDetailLevel = (value) => {
    const labels = { none: 'Nur Positionen', weeks: 'Einzelnachweis je Kalenderwoche', days: 'Einzelnachweis je Tag', entries: 'Einzelnachweis je Buchung' };
    return labels[value] || value;
  };
  
  // Helper: Formatiert position_split für Anzeige (L2)
  const formatPositionSplit = (value) => {
    const labels = { combined: 'Eine Position, Tätigkeiten im Text', per_activity: 'Eine Position je Tätigkeit' };
    return labels[value] || value;
  };
  
  // Helper: Formatiert reference_type für Anzeige
  const formatReferenceType = (value) => {
    const labels = { none: 'Keine', po: 'Bestellnummer / PO', contract: 'Vertragsnummer', quote: 'Angebotsnummer' };
    return labels[value] || value;
  };
  
  // Helper: Formatiert mileage_mode für Anzeige
  const formatMileageMode = (value) => {
    const labels = { per_km: 'Nach Kilometern', flat_per_trip: 'Pauschale je Anfahrt', none: 'Nicht berechnen' };
    return labels[value] || value;
  };
  
  // Bankkonto (X2): Standardkonto und aktuell gewähltes Konto
  const defaultBankAccount = bankAccounts.find((a) => a.is_default && a.active) || null;
  const selectedBankAccount = bankAccounts.find((a) => a.id === formData.bank_account_id) || defaultBankAccount;

  // Helper: Resolved mileage mode
  const getResolvedMileageMode = () => {
    return formData.mileage_mode || invoiceDefaults.mileage_mode || 'per_km';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="customers-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Kunden</h1>
          <p className="text-muted-foreground mt-1">
            {customers.length} {customers.length === 1 ? 'Kunde' : 'Kunden'}
          </p>
        </div>

        {canManage && (
          <Button
            onClick={openCreateDialog}
            data-testid="create-customer-button"
            className="bg-indigo-500 hover:bg-indigo-600 text-white glow-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Kunde
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          type="text"
          data-testid="search-customers"
          placeholder="Kunden suchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-input border-border text-foreground"
        />
      </div>

      {/* Customers Table */}
      <div className="rounded-xl border border-border overflow-hidden bg-card/30">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground font-medium">Kunde</TableHead>
                <TableHead className="text-muted-foreground font-medium">Ort</TableHead>
                <TableHead className="text-muted-foreground font-medium">Ansprechpartner</TableHead>
                <TableHead className="text-muted-foreground font-medium">Aktive Aufträge</TableHead>
                {canManage && (
                  <TableHead className="text-muted-foreground font-medium">Standardsatz</TableHead>
                )}
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.map((customer) => (
                <TableRow
                  key={customer.id}
                  className="group hover:bg-muted/30 cursor-pointer"
                  onClick={() => openDetailView(customer)}
                >
                  {/* Kunde */}
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">{customer.name}</span>
                        {customer.is_internal && (
                          <Badge className="w-fit mt-1 bg-zinc-500/20 text-zinc-500 border-zinc-500/30 text-xs">
                            Intern – nicht abrechnen
                          </Badge>
                        )}
                        {hasCustomInvoiceSettings(customer) && (
                          <Badge className="w-fit mt-1 bg-indigo-500/20 text-indigo-400 border-indigo-500/30 text-xs">
                            Eigene Rechnungseinstellungen
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  {/* Ort */}
                  <TableCell className="py-3 text-sm text-muted-foreground">
                    {formatLocation(customer)}
                  </TableCell>

                  {/* Ansprechpartner */}
                  <TableCell className="py-3 text-sm text-muted-foreground">
                    {getPrimaryContact(customer)}
                  </TableCell>

                  {/* Aktive Aufträge */}
                  <TableCell className="py-3">
                    <Badge variant="outline" className="bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20">
                      {customer.active_projects_count || 0}
                    </Badge>
                  </TableCell>

                  {/* Standardsatz */}
                  {canManage && (
                    <TableCell className="py-3 text-sm text-muted-foreground">
                      {customer.standard_rate != null
                        ? `${customer.standard_rate.toFixed(2)} €/h`
                        : '—'}
                    </TableCell>
                  )}

                  {/* Aktionen */}
                  <TableCell className="py-3 text-right">
                    {canManage && (
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(customer);
                          }}
                          className="text-muted-foreground hover:text-foreground h-7 px-2"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(customer.id);
                          }}
                          className="text-muted-foreground hover:text-red-400 h-7 px-2"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {filteredCustomers.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery ? 'Keine Kunden gefunden' : 'Noch keine Kunden vorhanden'}
            </p>
          </div>
        )}
      </div>

      {/* Detail View Dialog (Read-Only) */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailCustomer && (
            <>
              <DialogHeader>
                <DialogTitle className="text-foreground font-heading flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  {detailCustomer.name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Beschreibung */}
                {detailCustomer.description && (
                  <p className="text-muted-foreground">{detailCustomer.description}</p>
                )}

                {/* Kontakt & Adresse */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-indigo-500" />
                    Kontakt & Adresse
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                    {detailCustomer.contact_email && (
                      <div>
                        <p className="text-xs text-muted-foreground">E-Mail</p>
                        <p className="text-sm text-foreground">{detailCustomer.contact_email}</p>
                      </div>
                    )}
                    {detailCustomer.contact_phone && (
                      <div>
                        <p className="text-xs text-muted-foreground">Telefon</p>
                        <p className="text-sm text-foreground">{detailCustomer.contact_phone}</p>
                      </div>
                    )}
                    {formatFullAddress(detailCustomer) && (
                      <div className="md:col-span-2">
                        <p className="text-xs text-muted-foreground">Adresse</p>
                        {formatFullAddress(detailCustomer).map((line, i) => (
                          <p key={i} className="text-sm text-foreground">{line}</p>
                        ))}
                      </div>
                    )}
                    {detailCustomer.location_note && (
                      <div className="md:col-span-2">
                        <p className="text-xs text-muted-foreground">Standorthinweis</p>
                        <p className="text-sm text-foreground">{detailCustomer.location_note}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rechnung & Steuer */}
                {(detailCustomer.ust_id || detailCustomer.steuernummer || formatBillingAddress(detailCustomer) || detailCustomer.bg_zuordnung || detailCustomer.wz_code || detailCustomer.invoice_email || detailCustomer.customer_number || detailCustomer.tax_category) && (
                  <div className="space-y-3 border-t border-border pt-4">
                    <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                      <FileText className="w-4 h-4 text-indigo-500" />
                      Rechnung & Steuer
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                      {detailCustomer.customer_number && (
                        <div>
                          <p className="text-xs text-muted-foreground">Kundennummer</p>
                          <p className="text-sm text-foreground">{detailCustomer.customer_number}</p>
                        </div>
                      )}
                      {detailCustomer.invoice_email && (
                        <div>
                          <p className="text-xs text-muted-foreground">Rechnungs-E-Mail</p>
                          <p className="text-sm text-foreground">{detailCustomer.invoice_email}</p>
                        </div>
                      )}
                      {detailCustomer.tax_category && (
                        <div>
                          <p className="text-xs text-muted-foreground">Steuerkategorie</p>
                          <p className="text-sm text-foreground">{taxCategoryLabel(detailCustomer.tax_category)}</p>
                        </div>
                      )}
                      {detailCustomer.ust_id && (
                        <div>
                          <p className="text-xs text-muted-foreground">USt-IdNr.</p>
                          <p className="text-sm text-foreground">{detailCustomer.ust_id}</p>
                        </div>
                      )}
                      {detailCustomer.steuernummer && (
                        <div>
                          <p className="text-xs text-muted-foreground">Steuernummer</p>
                          <p className="text-sm text-foreground">{detailCustomer.steuernummer}</p>
                        </div>
                      )}
                      {formatBillingAddress(detailCustomer) && (
                        <div className="md:col-span-2">
                          <p className="text-xs text-muted-foreground">Abweichende Rechnungsanschrift</p>
                          {formatBillingAddress(detailCustomer).map((line, i) => (
                            <p key={i} className="text-sm text-foreground">{line}</p>
                          ))}
                        </div>
                      )}
                      {detailCustomer.bg_zuordnung && (
                        <div>
                          <p className="text-xs text-muted-foreground">Berufsgenossenschaft</p>
                          <p className="text-sm text-foreground">{detailCustomer.bg_zuordnung}</p>
                        </div>
                      )}
                      {detailCustomer.wz_code && (
                        <div>
                          <p className="text-xs text-muted-foreground">WZ-Code</p>
                          <p className="text-sm text-foreground">{detailCustomer.wz_code}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Kennzahlen */}
                <div className="space-y-3 border-t border-border pt-4">
                  <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Info className="w-4 h-4 text-indigo-500" />
                    Kennzahlen
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                    <div>
                      <p className="text-xs text-muted-foreground">Aktive Aufträge</p>
                      <p className="text-sm text-foreground">{detailCustomer.active_projects_count || 0}</p>
                    </div>
                    {canManage && detailCustomer.standard_rate != null && (
                      <div>
                        <p className="text-xs text-muted-foreground">Standardsatz</p>
                        <p className="text-sm text-foreground">{detailCustomer.standard_rate.toFixed(2)} €/h</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Ansprechpartner */}
                <div className="space-y-3 border-t border-border pt-4">
                  <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-500" />
                    Ansprechpartner
                  </h4>
                  <div className="pl-6">
                    {detailCustomer.contacts && detailCustomer.contacts.length > 0 ? (
                      <div className="space-y-2">
                        {detailCustomer.contacts.map((contact, i) => (
                          <div key={i} className="text-sm">
                            <span className="text-foreground">{contact.name || contact.email}</span>
                            {contact.role && (
                              <span className="text-muted-foreground ml-2">({contact.role})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Keine hinterlegt</p>
                    )}
                  </div>
                </div>

                {/* Standorte */}
                <div className="space-y-3 border-t border-border pt-4">
                  <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-indigo-500" />
                    Standorte
                  </h4>
                  <div className="pl-6">
                    {detailCustomer.sites && detailCustomer.sites.length > 0 ? (
                      <div className="space-y-2">
                        {detailCustomer.sites.map((site, i) => (
                          <div key={i} className="text-sm text-foreground">
                            {site.name || site.address || `Standort ${i + 1}`}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Keine hinterlegt</p>
                    )}
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 border-t border-border pt-4">
                <Button
                  variant="ghost"
                  onClick={() => setIsDetailOpen(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Schließen
                </Button>
                {canManage && (
                  <Button
                    onClick={() => {
                      setIsDetailOpen(false);
                      openEditDialog(detailCustomer);
                    }}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Bearbeiten
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog with Tabs */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-5xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">
              {editingCustomer ? 'Kunde bearbeiten' : 'Neuer Kunde'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="stammdaten" className="w-full">
              <TabsList className="w-full justify-start bg-muted/50 border border-border mb-4 flex-wrap">
                <TabsTrigger value="stammdaten" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                  Stammdaten
                </TabsTrigger>
                <TabsTrigger value="rechnung" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                  Rechnung
                </TabsTrigger>
                <TabsTrigger value="ansprechpartner" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                  Ansprechpartner
                </TabsTrigger>
                <TabsTrigger value="standorte" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                  Standorte
                </TabsTrigger>
                {canManage && (
                  <TabsTrigger value="saetze" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                    Sätze
                  </TabsTrigger>
                )}
                {canManage && (
                  <TabsTrigger value="zuschlaege" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
                    Zuschläge
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Tab 1: Stammdaten – wer ist der Kunde, wo sitzt er */}
              <TabsContent value="stammdaten" className="space-y-6 mt-0">
                <section className="space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Kunde</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-muted-foreground">Name wie auf der Rechnung <span className="text-red-500">*</span><BtTag code="BT-44" /></Label>
                      <Input
                        data-testid="customer-name-input"
                        value={formData.name}
                        onChange={(e) => set('name', e.target.value)}
                        required
                        className="bg-input border-border text-foreground"
                        placeholder="Vollständiger Firmenname mit Rechtsform"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Kundennummer<BtTag code="BT-46" /></Label>
                      <Input
                        value={formData.customer_number || ''}
                        onChange={(e) => set('customer_number', e.target.value)}
                        className="bg-input border-border text-foreground"
                        placeholder="optional"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-muted-foreground">Handelsname, falls abweichend<BtTag code="BT-45" /></Label>
                      <Input
                        value={formData.trading_name || ''}
                        onChange={(e) => set('trading_name', e.target.value)}
                        className="bg-input border-border text-foreground"
                        placeholder="optional"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Interne Beschreibung</Label>
                      <Input
                        data-testid="customer-description-input"
                        value={formData.description}
                        onChange={(e) => set('description', e.target.value)}
                        className="bg-input border-border text-foreground"
                        placeholder="z. B. LNG-Terminal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Kontakt E-Mail (allgemein)</Label>
                      <Input
                        type="email"
                        data-testid="customer-email-input"
                        value={formData.contact_email}
                        onChange={(e) => set('contact_email', e.target.value)}
                        className="bg-input border-border text-foreground"
                        placeholder="kontakt@firma.de"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Telefon</Label>
                      <Input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => set('contact_phone', e.target.value)}
                        className="bg-input border-border text-foreground"
                        placeholder="+49 123 456789"
                      />
                    </div>
                  </div>
                  {/* R6: interner Kunde */}
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer">
                    <Checkbox
                      checked={!!formData.is_internal}
                      onCheckedChange={(checked) => set('is_internal', checked === true)}
                      className="mt-0.5"
                      data-testid="customer-internal-checkbox"
                    />
                    <span className="text-sm">
                      <span className="font-medium text-foreground">Interner Kunde – nicht abrechnen</span>
                      <span className="block text-xs text-muted-foreground">
                        Z. B. „CTI Intern“: Stunden werden gebucht und in Berichten und Kalkulation ausgewertet, erscheinen aber nie unter „Abzurechnen“ und können nicht in eine Rechnung übernommen werden.
                      </span>
                    </span>
                  </label>
                </section>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-border pt-5">
                  <section className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Hauptanschrift</p>
                      <span className="text-xs text-muted-foreground">Sitz des Kunden</span>
                    </div>
                    <Input
                      value={formData.address_line1}
                      onChange={(e) => set('address_line1', e.target.value)}
                      className="bg-input border-border text-foreground"
                      placeholder="Straße und Hausnummer"
                    />
                    <Input
                      value={formData.address_line2}
                      onChange={(e) => set('address_line2', e.target.value)}
                      className="bg-input border-border text-foreground"
                      placeholder="Adresszusatz (optional)"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={formData.postal_code}
                        onChange={(e) => set('postal_code', e.target.value)}
                        className="bg-input border-border text-foreground"
                        placeholder="PLZ"
                      />
                      <Input
                        value={formData.city}
                        onChange={(e) => set('city', e.target.value)}
                        className="bg-input border-border text-foreground col-span-2"
                        placeholder="Ort"
                      />
                    </div>
                    <Input
                      value={formData.state}
                      onChange={(e) => set('state', e.target.value)}
                      className="bg-input border-border text-foreground"
                      placeholder="Bundesland (optional)"
                    />
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Land <span className="text-red-500">*</span><BtTag code="BT-55" /></Label>
                      <CountrySelect
                        testId="customer-country-select"
                        value={formData.country_code}
                        onChange={(v) => set('country_code', v)}
                      />
                    </div>
                  </section>

                  <section className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rechnungsanschrift</p>
                      <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                        <Checkbox
                          checked={showBillingAddress}
                          onCheckedChange={(checked) => setShowBillingAddress(checked === true)}
                        />
                        abweichend
                      </label>
                    </div>
                    {showBillingAddress ? (
                      <>
                        <Input
                          value={formData.billing_address_line1}
                          onChange={(e) => set('billing_address_line1', e.target.value)}
                          className="bg-input border-border text-foreground"
                          placeholder="Straße oder Abteilung (z. B. Kreditorenbuchhaltung)"
                        />
                        <Input
                          value={formData.billing_address_line2}
                          onChange={(e) => set('billing_address_line2', e.target.value)}
                          className="bg-input border-border text-foreground"
                          placeholder="Adresszusatz / Postfach (optional)"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            value={formData.billing_postal_code}
                            onChange={(e) => set('billing_postal_code', e.target.value)}
                            className="bg-input border-border text-foreground"
                            placeholder="PLZ"
                          />
                          <Input
                            value={formData.billing_city}
                            onChange={(e) => set('billing_city', e.target.value)}
                            className="bg-input border-border text-foreground col-span-2"
                            placeholder="Ort"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Land</Label>
                          <CountrySelect
                            value={formData.billing_country_code}
                            onChange={(v) => set('billing_country_code', v)}
                            inheritLabel={`wie Hauptanschrift (${formData.country_code || 'DE'})`}
                          />
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">Rechnungen gehen an die Hauptanschrift.</p>
                    )}
                    <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                      Diese Anschrift steht auf jeder Rechnung an den Kunden. Eine Konzerntochter mit eigener Buchhaltung bitte als eigenen Kunden anlegen.
                    </div>
                  </section>
                </div>

                <section className="space-y-3 border-t border-border pt-5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Regelbetreuung</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Berufsgenossenschaft</Label>
                      <Input
                        value={formData.bg_zuordnung}
                        onChange={(e) => set('bg_zuordnung', e.target.value)}
                        className="bg-input border-border text-foreground"
                        placeholder="z. B. BG Bau"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">WZ-Code</Label>
                      <Input
                        value={formData.wz_code}
                        onChange={(e) => set('wz_code', e.target.value)}
                        className="bg-input border-border text-foreground"
                        placeholder="z. B. 71.20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Standorthinweis</Label>
                      <Input
                        value={formData.location_note}
                        onChange={(e) => set('location_note', e.target.value)}
                        className="bg-input border-border text-foreground"
                        placeholder="z. B. Eingang Tor 3"
                      />
                    </div>
                  </div>
                </section>
              </TabsContent>

              {/* Tab 2: Rechnung – alles, was für jede Rechnung an diesen Kunden gilt */}
              <TabsContent value="rechnung" className="mt-0">
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6">
                  <div className="space-y-6 min-w-0">
                    <p className="text-xs text-muted-foreground">
                      Leere Felder erben den Firmenstandard – der geltende Wert steht grau im Feld.
                    </p>

                    <section className="space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rechnungsempfang</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Wohin die Rechnung geht und was der Kunde zum Zuordnen braucht.</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Rechnungs-E-Mail<BtTag code="BT-49" /></Label>
                          <Input
                            type="email"
                            data-testid="customer-invoice-email-input"
                            value={formData.invoice_email || ''}
                            onChange={(e) => set('invoice_email', e.target.value)}
                            className="bg-input border-border text-foreground"
                            placeholder="rechnungen@kunde.de"
                          />
                          <p className="text-xs text-muted-foreground">Empfang der E-Rechnung und Versand aus CTIme.</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Adressart<OriginBadge own={!!formData.einvoice_address_scheme} /></Label>
                          <Select
                            value={formData.einvoice_address_scheme || 'default'}
                            onValueChange={(v) => set('einvoice_address_scheme', v === 'default' ? null : v)}
                          >
                            <SelectTrigger className="bg-input border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="default">Standard (E-Mail)</SelectItem>
                              {ADDRESS_SCHEMES.map(([code, label]) => (
                                <SelectItem key={code} value={code}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Käuferreferenz<BtTag code="BT-10" /></Label>
                          <Input
                            value={formData.buyer_reference || ''}
                            onChange={(e) => set('buyer_reference', e.target.value)}
                            className="bg-input border-border text-foreground"
                            placeholder="nur wenn der Kunde eine vorgibt"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Kostenstelle / Kontierung<BtTag code="BT-19" /></Label>
                          <Input
                            value={formData.buyer_accounting_ref || ''}
                            onChange={(e) => set('buyer_accounting_ref', e.target.value)}
                            className="bg-input border-border text-foreground"
                            placeholder="optional"
                          />
                        </div>
                      </div>
                    </section>

                    <section className="space-y-3 border-t border-border pt-5">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Referenzen für alle Rechnungen</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Gilt für jeden Auftrag dieses Kunden. Bestellnummern einzelner Aufträge stehen am Auftrag.</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Rahmenvertrag<BtTag code="BT-12" /></Label>
                          <Input
                            value={formData.contract_ref || ''}
                            onChange={(e) => set('contract_ref', e.target.value)}
                            className="bg-input border-border text-foreground"
                            placeholder="optional"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Rahmenbestellung<BtTag code="BT-13" /></Label>
                          <Input
                            value={formData.order_ref || ''}
                            onChange={(e) => set('order_ref', e.target.value)}
                            className="bg-input border-border text-foreground"
                            placeholder="meist am Auftrag"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Angebot<BtTag code="BT-14" /></Label>
                          <Input
                            value={formData.quote_ref || ''}
                            onChange={(e) => set('quote_ref', e.target.value)}
                            className="bg-input border-border text-foreground"
                            placeholder="optional"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Hauptreferenz (im PDF hervorgehoben)<OriginBadge own={!!formData.reference_type} /></Label>
                          <Select
                            value={formData.reference_type || 'default'}
                            onValueChange={(v) => set('reference_type', v === 'default' ? null : v)}
                          >
                            <SelectTrigger className="bg-input border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="default">Firmenstandard ({formatReferenceType(invoiceDefaults.reference_type || 'none')})</SelectItem>
                              <SelectItem value="none">Keine</SelectItem>
                              <SelectItem value="po">Bestellnummer</SelectItem>
                              <SelectItem value="contract">Vertragsnummer</SelectItem>
                              <SelectItem value="quote">Angebotsnummer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-start gap-3 pt-7">
                          <Checkbox
                            id="require_reference"
                            checked={formData.require_reference === true}
                            onCheckedChange={(checked) => set('require_reference', checked ? true : (formData.require_reference !== null ? false : null))}
                            className="mt-0.5"
                          />
                          <div className="space-y-1">
                            <Label htmlFor="require_reference" className="text-foreground cursor-pointer">
                              Ohne diese Referenz keinen Rechnungsentwurf zulassen
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              Ohne Hauptreferenz gilt die Bestellnummer. Firmenstandard: {invoiceDefaults.require_reference ? 'Ja' : 'Nein'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="space-y-3 border-t border-border pt-5">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Zahlung</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Das gewählte Konto steht auf jeder Rechnung an diesen Kunden – im PDF und in der E-Rechnung.</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Zahlungsziel (Tage)<OriginBadge own={formData.payment_term_days != null} /></Label>
                          <Input
                            type="number"
                            min={0}
                            max={120}
                            value={formData.payment_term_days ?? ''}
                            onChange={(e) => set('payment_term_days', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                            placeholder={`Standard: ${invoiceDefaults.payment_term_days ?? 14} Tage (Firma)`}
                            className="bg-input border-border text-foreground"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Skonto</Label>
                          <Input disabled placeholder="folgt (X7)" className="bg-muted/40 border-dashed border-border" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Zahlung auf Konto<BtTag code="BT-84" /><OriginBadge own={!!formData.bank_account_id} /></Label>
                          <Select
                            value={formData.bank_account_id || 'default'}
                            onValueChange={(v) => set('bank_account_id', v === 'default' ? null : v)}
                          >
                            <SelectTrigger data-testid="customer-bank-account-select" className="bg-input border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="default">
                                Standardkonto{defaultBankAccount ? ` (${defaultBankAccount.label} ··${(defaultBankAccount.iban || '').slice(-4)})` : ''}
                              </SelectItem>
                              {bankAccounts.filter((a) => a.active || a.id === formData.bank_account_id).map((a) => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.label} ··{(a.iban || '').slice(-4)}{a.active ? '' : ' (deaktiviert)'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {selectedBankAccount && (
                            <p className="text-xs text-muted-foreground font-mono">{selectedBankAccount.iban_display}</p>
                          )}
                        </div>
                      </div>
                    </section>

                    <section className="space-y-3 border-t border-border pt-5">
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Steuer</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Normalfall Regelsatz. Abweichend z. B. für Unternehmer in anderen EU-Staaten (Reverse Charge).</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Steuerkategorie<BtTag code="BT-151" /><OriginBadge own={!!formData.tax_category} /></Label>
                          <Select
                            value={formData.tax_category || 'default'}
                            onValueChange={(v) => set('tax_category', v === 'default' ? null : v)}
                          >
                            <SelectTrigger data-testid="customer-tax-category-select" className="bg-input border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="default">Firmenstandard ({taxCategoryLabel(invoiceDefaults.default_tax_category || 'S')})</SelectItem>
                              {TAX_CATEGORIES.map(([code, label]) => (
                                <SelectItem key={code} value={code}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Steuersatz (%)<OriginBadge own={formData.vat_rate != null} /></Label>
                          {formData.tax_category && formData.tax_category !== 'S' ? (
                            <Input disabled value="0 %" className="bg-muted/40 border-border" />
                          ) : (
                            <Input
                              type="number"
                              step={0.1}
                              min={0}
                              max={100}
                              value={formData.vat_rate ?? ''}
                              onChange={(e) => set('vat_rate', e.target.value === '' ? null : parseFloat(e.target.value))}
                              placeholder={`Standard: ${invoiceDefaults.default_vat_rate ?? 19} % (Firma)`}
                              className="bg-input border-border text-foreground"
                            />
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">USt-IdNr. des Kunden<BtTag code="BT-48" /></Label>
                          <Input
                            value={formData.ust_id || ''}
                            onChange={(e) => set('ust_id', e.target.value)}
                            className="bg-input border-border text-foreground"
                            placeholder="DE123456789"
                          />
                          <p className="text-xs text-muted-foreground">Pflicht bei Reverse Charge.</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Steuernummer des Kunden</Label>
                          <Input
                            value={formData.steuernummer || ''}
                            onChange={(e) => set('steuernummer', e.target.value)}
                            className="bg-input border-border text-foreground"
                            placeholder="nur zur Information"
                          />
                        </div>
                      </div>
                      {formData.tax_category === 'AE' && (
                        <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                          Auf der Rechnung steht automatisch „Steuerschuldnerschaft des Leistungsempfängers“. Bei Kunden in anderen EU-Staaten sind die USt-IdNr. von CTI und vom Kunden Pflicht (§ 14a Abs. 1 UStG).
                        </div>
                      )}
                    </section>

                    <section className="space-y-3 border-t border-border pt-5">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Abrechnung und Fahrtkosten</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Abrechnungsrhythmus<OriginBadge own={!!formData.billing_interval} /></Label>
                          <Select
                            value={formData.billing_interval || 'default'}
                            onValueChange={(v) => set('billing_interval', v === 'default' ? null : v)}
                          >
                            <SelectTrigger className="bg-input border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="default">Firmenstandard ({formatBillingInterval(invoiceDefaults.billing_interval || 'monthly')})</SelectItem>
                              <SelectItem value="monthly">Monatlich</SelectItem>
                              <SelectItem value="quarterly">Quartalsweise</SelectItem>
                              <SelectItem value="none">Nach Aufwand</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Nachweis auf der Rechnung<OriginBadge own={!!formData.detail_level} /></Label>
                          <Select
                            value={formData.detail_level || 'default'}
                            onValueChange={(v) => set('detail_level', v === 'default' ? null : v)}
                          >
                            <SelectTrigger className="bg-input border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="default">Firmenstandard ({formatDetailLevel(invoiceDefaults.detail_level || 'none')})</SelectItem>
                              <SelectItem value="none">Nur Positionen</SelectItem>
                              <SelectItem value="weeks">Einzelnachweis je Kalenderwoche</SelectItem>
                              <SelectItem value="days">Einzelnachweis je Tag</SelectItem>
                              <SelectItem value="entries">Einzelnachweis je Buchung</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Aufteilung der Betreuungsstunden<OriginBadge own={!!formData.position_split} /></Label>
                          <Select
                            value={formData.position_split || 'default'}
                            onValueChange={(v) => set('position_split', v === 'default' ? null : v)}
                          >
                            <SelectTrigger className="bg-input border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="default">Firmenstandard ({formatPositionSplit(invoiceDefaults.position_split || 'combined')})</SelectItem>
                              <SelectItem value="combined">Eine Position, Tätigkeiten im Text</SelectItem>
                              <SelectItem value="per_activity">Eine Position je Tätigkeit</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">Gilt für Leistungskategorien mit Rechnungstext. Im Entwurf je Rechnung umstellbar.</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Fahrtkosten<OriginBadge own={!!formData.mileage_mode} /></Label>
                          <Select
                            value={formData.mileage_mode || 'default'}
                            onValueChange={(v) => set('mileage_mode', v === 'default' ? null : v)}
                          >
                            <SelectTrigger className="bg-input border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="default">Firmenstandard ({formatMileageMode(invoiceDefaults.mileage_mode || 'per_km')})</SelectItem>
                              <SelectItem value="per_km">Nach Kilometern</SelectItem>
                              <SelectItem value="flat_per_trip">Pauschale je Anfahrt</SelectItem>
                              <SelectItem value="none">Nicht berechnen</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {getResolvedMileageMode() === 'per_km' && (
                          <div className="space-y-2">
                            <Label className="text-muted-foreground">Kilometersatz (€)<OriginBadge own={formData.mileage_rate_per_km != null} /></Label>
                            <Input
                              type="number"
                              step={0.01}
                              min={0}
                              max={10}
                              value={formData.mileage_rate_per_km ?? ''}
                              onChange={(e) => set('mileage_rate_per_km', e.target.value === '' ? null : parseFloat(e.target.value))}
                              placeholder={`Standard: ${invoiceDefaults.mileage_rate_per_km ?? 0.30} € (Firma)`}
                              className="bg-input border-border text-foreground"
                            />
                          </div>
                        )}
                        {getResolvedMileageMode() === 'flat_per_trip' && (
                          <div className="space-y-2">
                            <Label className="text-muted-foreground">Pauschale je Anfahrt (€)<OriginBadge own={formData.mileage_flat_rate != null} /></Label>
                            <Input
                              type="number"
                              step={0.01}
                              min={0}
                              max={500}
                              value={formData.mileage_flat_rate ?? ''}
                              onChange={(e) => set('mileage_flat_rate', e.target.value === '' ? null : parseFloat(e.target.value))}
                              placeholder={`Standard: ${invoiceDefaults.mileage_flat_rate ?? 0} € (Firma)`}
                              className="bg-input border-border text-foreground"
                            />
                            <p className="text-xs text-muted-foreground">Hin- und Rückweg enthalten. Mehrere Fahrten am selben Tag für denselben Auftrag gelten als eine Anfahrt.</p>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>

                  <aside className="space-y-3">
                    {formData.is_internal ? (
                      <div className="p-3 rounded-lg border border-border bg-muted/50 text-sm text-muted-foreground">
                        Interner Kunde – es werden keine Rechnungen erstellt, die E-Rechnungs-Prüfung entfällt.
                      </div>
                    ) : (
                      <CustomerEInvoiceStatus api={api} customerId={editingCustomer?.id} />
                    )}
                    <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                      Änderungen gelten für neue Rechnungsentwürfe. Ausgestellte Rechnungen behalten ihre Werte.
                    </div>
                  </aside>
                </div>
              </TabsContent>

              {/* Tab 3: Ansprechpartner */}
              <TabsContent value="ansprechpartner" className="mt-0 space-y-4">
                {formData.contacts.length === 0 ? (
                  <div className="p-6 rounded-lg bg-muted/50 border border-border text-center">
                    <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Noch keine Ansprechpartner. Fügen Sie den ersten hinzu.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.contacts.map((contact, idx) => (
                      <div key={idx} className="p-4 rounded-lg border border-border bg-muted/30 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Ansprechpartner {idx + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = formData.contacts.filter((_, i) => i !== idx);
                              setFormData({ ...formData, contacts: updated });
                            }}
                            className="text-muted-foreground hover:text-red-400 h-7 px-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={contact.name || ''}
                            onChange={(e) => {
                              const updated = formData.contacts.map((c, i) => 
                                i === idx ? { ...c, name: e.target.value } : c
                              );
                              setFormData({ ...formData, contacts: updated });
                            }}
                            className="bg-input border-border text-foreground"
                            placeholder="Name"
                          />
                          <Input
                            value={contact.role || ''}
                            onChange={(e) => {
                              const updated = formData.contacts.map((c, i) => 
                                i === idx ? { ...c, role: e.target.value } : c
                              );
                              setFormData({ ...formData, contacts: updated });
                            }}
                            className="bg-input border-border text-foreground"
                            placeholder="Rolle (z.B. SiFa-Koordinator)"
                          />
                          <Input
                            type="email"
                            value={contact.email || ''}
                            onChange={(e) => {
                              const updated = formData.contacts.map((c, i) => 
                                i === idx ? { ...c, email: e.target.value } : c
                              );
                              setFormData({ ...formData, contacts: updated });
                            }}
                            className="bg-input border-border text-foreground"
                            placeholder="E-Mail"
                          />
                          <Input
                            type="tel"
                            value={contact.phone || ''}
                            onChange={(e) => {
                              const updated = formData.contacts.map((c, i) => 
                                i === idx ? { ...c, phone: e.target.value } : c
                              );
                              setFormData({ ...formData, contacts: updated });
                            }}
                            className="bg-input border-border text-foreground"
                            placeholder="Telefon"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const newContact = { name: '', role: '', email: '', phone: '' };
                    setFormData({ ...formData, contacts: [...formData.contacts, newContact] });
                  }}
                  className="w-full border-dashed border-border text-muted-foreground hover:text-foreground hover:border-indigo-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ansprechpartner hinzufügen
                </Button>
              </TabsContent>

              {/* Tab 4: Standorte */}
              <TabsContent value="standorte" className="mt-0 space-y-4">
                {formData.sites.length === 0 ? (
                  <div className="p-6 rounded-lg bg-muted/50 border border-border text-center">
                    <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Noch keine Standorte hinterlegt.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.sites.map((site, idx) => (
                      <div key={idx} className="p-4 rounded-lg border border-border bg-muted/30 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Standort {idx + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = formData.sites.filter((_, i) => i !== idx);
                              setFormData({ ...formData, sites: updated });
                            }}
                            className="text-muted-foreground hover:text-red-400 h-7 px-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={site.name || ''}
                            onChange={(e) => {
                              const updated = formData.sites.map((s, i) => 
                                i === idx ? { ...s, name: e.target.value } : s
                              );
                              setFormData({ ...formData, sites: updated });
                            }}
                            className="bg-input border-border text-foreground"
                            placeholder="Name/Bezeichnung (z.B. Werk Stade)"
                          />
                          <Input
                            value={site.address || ''}
                            onChange={(e) => {
                              const updated = formData.sites.map((s, i) => 
                                i === idx ? { ...s, address: e.target.value } : s
                              );
                              setFormData({ ...formData, sites: updated });
                            }}
                            className="bg-input border-border text-foreground"
                            placeholder="Straße und Hausnummer"
                          />
                          <Input
                            value={site.postal_code || ''}
                            onChange={(e) => {
                              const updated = formData.sites.map((s, i) => 
                                i === idx ? { ...s, postal_code: e.target.value } : s
                              );
                              setFormData({ ...formData, sites: updated });
                            }}
                            className="bg-input border-border text-foreground"
                            placeholder="PLZ"
                          />
                          <Input
                            value={site.city || ''}
                            onChange={(e) => {
                              const updated = formData.sites.map((s, i) => 
                                i === idx ? { ...s, city: e.target.value } : s
                              );
                              setFormData({ ...formData, sites: updated });
                            }}
                            className="bg-input border-border text-foreground"
                            placeholder="Ort"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const newSite = { name: '', address: '', postal_code: '', city: '' };
                    setFormData({ ...formData, sites: [...formData.sites, newSite] });
                  }}
                  className="w-full border-dashed border-border text-muted-foreground hover:text-foreground hover:border-indigo-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Standort hinzufügen
                </Button>
              </TabsContent>

              {/* Tab 5: Sätze (nur canManage) */}
              {canManage && (
                <TabsContent value="saetze" className="mt-0">
                  {editingCustomer ? (
                    <RateManager
                      scope="customer"
                      customerId={editingCustomer.id}
                      title="Kundensatz (Basissatz)"
                    />
                  ) : (
                    <div className="p-4 rounded-lg bg-muted/50 border border-border flex items-start gap-3">
                      <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground">
                        Sätze können nach dem Speichern hinterlegt werden.
                      </p>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Tab 6: Zuschläge (nur canManage) */}
              {canManage && (
                <TabsContent value="zuschlaege" className="mt-0">
                  {editingCustomer ? (
                    <SurchargeRulesPanel customerId={editingCustomer.id} />
                  ) : (
                    <div className="p-4 rounded-lg bg-muted/50 border border-border flex items-start gap-3">
                      <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground">
                        Zuschläge können nach dem Speichern konfiguriert werden.
                      </p>
                    </div>
                  )}
                </TabsContent>
              )}
            </Tabs>

            <DialogFooter className="gap-2 mt-6 pt-4 border-t border-border">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsDialogOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                data-testid="save-customer-button"
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                {editingCustomer ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
